import {
  CumplimientoFiltro,
  ProgramacionPendiente,
  CumplimientoRequest,
  CumplimientoResponse,
  Cumplimiento,
  SaldoResponse,
  ConfiguracionCumplimientoResponse,
  CursorPageResponse,
  EnvioInformeRequest,
  EnvioInformeResultado,
  InformePendiente,
  HistorialCumplimiento,
  ObservacionRecomendacion,
  ActaCumplimiento,
  AsociarActaResponse,
  DatosActaRequest,
  GenerarActaResponse,
  RiesgoPotencialFiltro,
  RiesgoPotencialConteo,
  RiesgoPotencialPage,
  RiesgoPotencialDetalle,
  ActualizarRiesgoPotencialRequest,
  RiesgoSeguimiento,
  GuardarRiesgoSeguimientoRequest,
  EvidenciaSeguimientoResponse,
  CursorDireccion
} from '../../model/cumplimiento/cumplimiento';

/**
 * Puerto Abstracto: CumplimientoRepository
 * 
 * Define el contrato para gestión de cumplimientos de servicio.
 * Migrado desde: FilterOrdenesProgActivity, CreateOrUpdateCumplimientoActivity
 * 
 * Arquitectura Hexagonal:
 * - Este puerto está en el dominio
 * - HttpCumplimientoRepository (adaptador) lo implementa en infraestructura
 * 
 * @author Emerson Rodriguez - Ingeneo
 */
export abstract class CumplimientoRepository {

  // === Programaciones Pendientes ===

  /**
   * Lista programaciones pendientes de cumplimiento con filtros y paginación
   * keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
   *
   * Legacy: FilterOrdenesProgViewImpl.java → stProgramaciones (tabla)
   *
   * El backend SÍ sigue calculando totalElements para este endpoint (cuenta
   * en paralelo al contenido en cada request).
   *
   * @param filtro Filtros de búsqueda (todos opcionales)
   * @param cursor Cursor de la página (null = primera página)
   * @param size Tamaño de página
   * @param direction 'NEXT' | 'PREVIOUS' | 'LAST'
   */
  abstract listarProgramacionesPendientes(
    filtro: CumplimientoFiltro,
    cursor: string | null,
    size: number,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<CursorPageResponse<ProgramacionPendiente>>;

  /**
   * Obtiene el detalle de una programación pendiente.
   * 
   * @param nmconsecutivo ID de la programación
   */
  abstract obtenerProgramacion(nmconsecutivo: number): Promise<ProgramacionPendiente>;

  // === CRUD Cumplimientos ===

  /**
   * Registra un nuevo cumplimiento de servicio.
   * 
   * Legacy: CreateOrUpdateCumplimientoViewImpl.java -> botón Grabar
   * 
   * Validaciones del backend:
   * - RN-01: Cantidad ejecutada <= saldo por ejecutar
   * - RN-02: Transporte obligatorio para tipo CAMPO con pago=S
   * - RN-05: Justificación obligatoria para cumplimientos tardíos
   * - RN-06: Restricción CLICK Software
   * 
   * @param request Datos del cumplimiento
   */
  abstract registrarCumplimiento(request: CumplimientoRequest): Promise<CumplimientoResponse>;

  /**
   * Obtiene el detalle de un cumplimiento registrado.
   * 
   * Legacy: CreateOrUpdateCumplimientoViewImpl.java (modo edición)
   * 
   * @param nmconsecutivo ID del cumplimiento
   */
  abstract obtenerCumplimiento(nmconsecutivo: number): Promise<CumplimientoResponse>;

  /**
   * Lista los cumplimientos de una programación.
   * 
   * @param nmconsecutivoProgramacion ID de la programación
   */
  abstract listarCumplimientosProgramacion(nmconsecutivoProgramacion: number): Promise<Cumplimiento[]>;

  /**
   * Actualiza los datos de contacto de un cumplimiento.
   *
   * Backend: PUT /api/servicios/ordenes-servicio/cumplimientos/{id}/contacto
   *
   * @param nmconsecutivoCumplimiento ID del cumplimiento
   * @param nombreContacto Nombre del contacto
   * @param emailContacto Correo del contacto
   */
  abstract actualizarContactoCumplimiento(
    nmconsecutivoCumplimiento: number,
    nombreContacto: string,
    emailContacto: string
  ): Promise<boolean>;

  // === Saldo de Orden ===

  /**
   * Obtiene el saldo por ejecutar de una orden de servicio.
   * 
   * Legacy: SP_GET_SALDO_EJECUTAR (PCK_PSC_UTILIDADES)
   * 
   * @param nmconsecOrdenServDetalle ID del detalle de orden de servicio
   */
  abstract obtenerSaldoOrden(nmconsecOrdenServDetalle: number): Promise<SaldoResponse>;

  /**
   * Obtiene configuración funcional de Cumplimientos para consumo del frontend.
   *
   * Backend: GET /api/servicios/orientacionesProgramadas/configuracion
   */
  abstract obtenerConfiguracionCumplimiento(): Promise<ConfiguracionCumplimientoResponse>;

  // === Envío de Informes (P8/FileNet) ===

  /**
   * Lista los cumplimientos pendientes de envío de informe.
   * 
   * Legacy: EnviarInformeViewImpl.java -> tabla de informes pendientes
   * Backend: GET /api/servicios/informes-cumplimiento
   * 
   * @param filtros Filtros opcionales de búsqueda
   */
  abstract listarInformesPendientes(filtros?: Record<string, string>): Promise<InformePendiente[]>;

  /**
   * Envía un informe con documento adjunto a P8/FileNet.
   * 
   * Legacy: EnviarInformeActivity.java -> botón Enviar
   * Backend: POST /api/servicios/informes-cumplimiento/enviar (multipart)
   * 
   * Flujo:
   * 1. Convierte archivo a Base64
   * 2. Crea documento en P8
   * 3. Ejecuta SPs de envío
   * 4. Actualiza cumplimientos con idDocumentoP8
   * 
   * @param request Datos del informe y archivo
   */
  abstract enviarInforme(request: EnvioInformeRequest): Promise<EnvioInformeResultado>;
  
  // === Historial de Cumplimientos (Legacy: ListarCumplimientosActivity.java) ===
  
  /**
   * Lista los cumplimientos de una programación para el modal de historial.
   * 
   * Legacy: ListarCumplimientosActivity.listarCumplimientos()
   * Backend: GET /api/cumplimientos/programados/{id}/cumplimientos
   * 
   * Incluye datos adicionales para las acciones del modal:
   * - feprogramacion, nmconsecutivoTipoCita, cdcontrato (para validacionActa)
   * - cdconsecutivoActa, dsestadoActa (para estado y descarga PDF)
   * 
   * @param nmconsecutivoProgramacion ID de la programación
   */
  abstract listarHistorialCumplimientos(nmconsecutivoProgramacion: number): Promise<HistorialCumplimiento[]>;
  
  /**
   * Obtiene la observación de un cumplimiento.
   * 
   * Legacy: ListarCumplimientosActivity.getObservacionesCumplimiento()
   * Backend: GET /api/cumplimientos/{id}/observacion
   * 
   * @param nmconsecutivoCumplimiento ID del cumplimiento
   */
  abstract obtenerObservacion(nmconsecutivoCumplimiento: number): Promise<string>;
  
  /**
   * Obtiene la recomendación de un cumplimiento.
   * 
   * Legacy: ListarCumplimientosActivity.getRecomendacionesCumplimiento()
   * Backend: GET /api/cumplimientos/{id}/recomendacion
   * 
   * @param nmconsecutivoCumplimiento ID del cumplimiento
   */
  abstract obtenerRecomendacion(nmconsecutivoCumplimiento: number): Promise<string>;
  
  /**
   * Obtiene observación y recomendación en una sola llamada.
   * 
   * Optimización: Combina getObservacionesCumplimiento + getRecomendacionesCumplimiento
   * Backend: GET /api/cumplimientos/{id}/obs-rec
   * 
   * @param nmconsecutivoCumplimiento ID del cumplimiento
   */
  abstract obtenerObservacionYRecomendacion(nmconsecutivoCumplimiento: number): Promise<ObservacionRecomendacion>;
  

  // === Riesgo Potencial ===

  /**
   * Obtiene conteo agregado de riesgos potenciales (total/abiertos/cerrados).
   *
   * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales/conteo
   */
  abstract contarRiesgosPotenciales(filtro: RiesgoPotencialFiltro): Promise<RiesgoPotencialConteo>;

  /**
   * Lista riesgos potenciales con filtros y paginación keyset/cursor.
   *
   * Legacy: RiesgoPotencialViewImpl.onClickBuscar() -> tabla stRiesgoPotencial
   * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales
   *
   * @param filtro Filtros de búsqueda (al menos uno es obligatorio, regla del backend)
   * @param size Tamaño de página
   * @param cursor Cursor de la página actual (null = primera página)
   * @param direction Dirección de navegación (NEXT | PREVIOUS)
   */
  abstract listarRiesgosPotenciales(
    filtro: RiesgoPotencialFiltro,
    size: number,
    cursor?: string | null,
    direction?: CursorDireccion
  ): Promise<RiesgoPotencialPage>;

  /**
   * Obtiene el detalle de un riesgo potencial.
   *
   * Legacy: RiesgoPotencialActivity.seleccionarRegistro() -> SeguimientoRiesgoPotPlace
   * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}
   *
   * @param idRiesgo ID del riesgo
   */
  abstract obtenerRiesgoPotencial(idRiesgo: number): Promise<RiesgoPotencialDetalle>;

  /**
   * Actualiza el detalle editable de un riesgo potencial: situación, recomendación y
   * riesgo que le aplica; y, sólo en alertas «sin OC», asesor y profesional de prevención.
   *
   * Legacy: SeguimientoRiesgoPotViewImpl.onClickAceptar() -> comando guardarRiesgoPot
   * Backend: PUT /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}
   *
   * @param idRiesgo ID del riesgo
   * @param request Campos editables del diálogo «Modificar Detalle»
   */
  abstract actualizarRiesgoPotencial(
    idRiesgo: number,
    request: ActualizarRiesgoPotencialRequest
  ): Promise<RiesgoPotencialDetalle>;

  /**
   * Lista el histórico de seguimientos de un riesgo potencial.
   *
   * Legacy: SeguimientoRiesgoPotActivity.buscarSeguimiento()
   * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}/seguimientos
   *
   * @param idRiesgo ID del riesgo
   */
  abstract listarSeguimientosRiesgo(idRiesgo: number): Promise<RiesgoSeguimiento[]>;

  /**
   * Registra un seguimiento (o una reapertura) de un riesgo potencial.
   *
   * Legacy: SeguimientoRiesgoPotActivity.guardarSeguimineto()
   * Backend: POST /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}/seguimientos
   *
   * @param idRiesgo ID del riesgo
   * @param request Datos del seguimiento
   */
  abstract guardarSeguimientoRiesgo(
    idRiesgo: number,
    request: GuardarRiesgoSeguimientoRequest
  ): Promise<RiesgoSeguimiento>;

  /**
   * Carga evidencia P8 para el seguimiento de un riesgo potencial y retorna el
   * id de documento a incluir como `cddocumento` al guardar el seguimiento.
   *
   * Legacy: SeguimientoRiesgoPotViewImpl.uploaderEvidencia
   * Backend: POST /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}/evidencia
   *
   * @param idRiesgo ID del riesgo
   * @param archivoContenido Contenido del archivo en Base64 (sin prefijo data:)
   * @param archivoNombre Nombre del archivo
   * @param archivoContentType Content-Type del archivo
   */
  abstract cargarEvidenciaSeguimientoRiesgo(
    idRiesgo: number,
    archivoContenido: string,
    archivoNombre: string,
    archivoContentType: string
  ): Promise<EvidenciaSeguimientoResponse>;

  // === Acta (Legacy: ListarCumplimientosActivity.descargarActa360 / asociarActas) ===
  
  /**
   * Busca el acta asociada a un cumplimiento.
   * 
   * Legacy: AdminCumplimActaHandler.searchActaGirop()
   * Backend: GET /api/cumplimientos/{id}/acta
   * 
   * @param nmconsecutivoCumplimiento ID del cumplimiento
   * @return Acta encontrada o null si no existe
   */
  abstract buscarActaCumplimiento(nmconsecutivoCumplimiento: number): Promise<ActaCumplimiento | null>;
  
  /**
   * Asocia un acta a un cumplimiento via servicio externo.
   * 
   * Legacy: AdminCumplimientoHandler.asociarActasCumplimientos()
   * Backend: POST /api/servicios/ordenes-servicio/programaciones/0/asociar-acta
   * 
   * @param idCumplimiento ID del cumplimiento
   * @param consecutivoActa Consecutivo del acta
   * @return Resultado con exito y mensaje
   */
  abstract asociarActa(idCumplimiento: string, consecutivoActa: string): Promise<AsociarActaResponse>;

  /**
   * Reintenta la generación del acta 360 para un cumplimiento ya guardado.
   *
   * Backend: POST /api/servicios/orientacionesProgramadas/{id}/generar-acta
   *
   * @param nmconsecutivoCumplimiento Consecutivo del cumplimiento
   * @param datosActa Datos del acta para reenviar al backend
   */
  abstract reintentarGenerarActa(
    nmconsecutivoCumplimiento: number,
    datosActa: DatosActaRequest
  ): Promise<GenerarActaResponse>;
  
  /**
   * Obtiene la URL para descargar el PDF del acta.
   * 
   * Legacy: ConsumeServiceNoReturnAction.descargarActa()
   * Backend: GET /api/cumplimientos/{id}/descargar-acta (returns PDF)
   * 
   * @param nmconsecutivoCumplimiento ID del cumplimiento
   * @return URL del endpoint de descarga
   */
  abstract getDescargarActaUrl(nmconsecutivoCumplimiento: number): string;

  /**
   * Descarga el PDF del acta de un cumplimiento y dispara la descarga en el
   * navegador (blob), sin abrir ninguna pestaña/ventana externa.
   *
   * Usado como fallback cuando el acta aún no tiene documento en P8
   * (cdidP8Preliminar), para que la experiencia de descarga sea idéntica sin
   * importar si el acta se sirve desde P8 o desde G360 (el backend resuelve
   * internamente cdconsecutivoActa/idActa360).
   *
   * Backend: GET /api/cumplimientos/{id}/descargar-acta (returns PDF binary)
   *
   * @param nmconsecutivoCumplimiento ID del cumplimiento
   * @param nombreSugerido Nombre sugerido para el archivo descargado
   */
  abstract descargarActaPdf(nmconsecutivoCumplimiento: number, nombreSugerido?: string): Promise<void>;
}


